/**
 * The two calls the pre-consultation form makes, and — with the enquiry form's
 * pair — the only ones in the app that carry no bearer token.
 *
 * `api.get`/`api.post` set `Authorization` only when a token is passed, so a
 * public call is one made without. Nothing here may reach for `getToken()`:
 * there is no session on this screen and no workspace behind it. The token in
 * the path is the whole of the authorisation, and it belongs to the person
 * holding the link rather than to anybody signed in.
 *
 * snake_case stops here, as in every other service.
 */

import { api, API_ENDPOINTS, withValidationDetail } from "../../../lib/api";
import { ApiError } from "../../../lib/http";
// A deep import rather than the brand barrel: that barrel pulls the whole My
// brand screen in behind it, and this needs the one function that turns a
// stored file's path into a URL rooted at the API's origin instead of at this
// page's. The enquiry service reaches for it the same way.
import { mediaUrl } from "../../brand/services/fileService";
import { heightInCm } from "../preconsultationMeasures";
import { PRECONSULTATION_FALLBACK_OPTIONS } from "../preconsultationSteps";
// The submit answers with the whole form record, which is the same shape the
// practitioner's side already reads off a client — so it is mapped by the same
// function rather than by a second copy of forty fields. Only the mapper and
// its types are taken; nothing in that module is called from here, and nothing
// here gains a session by importing it.
import {
  toPreconsultationFormSummary,
  type PreconsultationFormRecord,
  type PreconsultationFormSummary,
} from "./preconsultationFormsService";
import type {
  CatalogRow,
  PractitionerServiceRow,
  PreconsultationAnswers,
  PreconsultationCatalog,
  PreconsultationCatalogs,
  PreconsultationFieldConfigResponse,
  PreconsultationFields,
  PreconsultationForm,
  PreconsultationFormResponse,
  PreconsultationKnownResponse,
  PreconsultationPayload,
  PreconsultationPreviousAnswersResponse,
} from "../types/preconsultation.types";

/**
 * One catalog: the rows the backend sent, and the names to draw from them.
 *
 * Where it sent none, the form's own list stands in — as names only, with no
 * rows behind them. Two of the five are always in that state, and it decides
 * how the field behaves: a list with ids can be resolved on the way out and is
 * offered as a closed set, while one without is free text the endpoint takes
 * as typed. See `toPreconsultationPayload`.
 */
const catalogOf = (
  rows: CatalogRow[] | null | undefined,
  fallback: readonly string[]
): PreconsultationCatalog => {
  const kept = (rows ?? []).filter((row) => row?.name?.trim());

  return kept.length > 0
    ? { rows: kept, names: kept.map((row) => row.name.trim()) }
    : { rows: [], names: [...fallback] };
};

/**
 * A practice's services as catalog rows, under the `public_id` that identifies
 * one. Rows the backend sent without a name are dropped by `catalogOf`, which
 * is where they would be drawn from.
 */
const serviceRows = (
  services: PractitionerServiceRow[] | null | undefined
): CatalogRow[] =>
  (services ?? []).map((service) => ({
    id: (service?.public_id ?? "").trim(),
    name: (service?.name ?? "").trim(),
  }));

/**
 * The Goal field's list: the services this practice has added, and nothing
 * else.
 *
 * No list of this app's own behind it, unlike every other field here. A goal
 * goes up as the `public_id` of one of the practice's services, so a name that
 * came from anywhere else resolves against no row and is sent as nothing — a
 * client shown "Weight loss" off a list this app invented would be told their
 * goal was recorded when it was not. An empty field says the truth instead,
 * and there is nothing for it to cost: the field is locked, and what it draws
 * is what the practitioner enrolled them against rather than a question.
 *
 * What they are already signed up against is folded in behind what the
 * practice currently offers. A service retired since they were enrolled is not
 * served under `services` any more, and leaving it out here would drop the
 * goal off a returned form the moment it was sent back up — silently, since
 * the client cannot see the field to notice. Matched by name, which is what
 * `rowOf` resolves on, and the first row of a name wins: the practice's own
 * id is the one to send where both carry one.
 */
const goalRows = (response: PreconsultationFormResponse): CatalogRow[] => {
  const rows: CatalogRow[] = [];
  const seen = new Set<string>();

  const keep = (row: CatalogRow) => {
    const key = row.name.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  };

  serviceRows(response.services).forEach(keep);
  serviceRows(response.existing_details?.services).forEach(keep);
  serviceRows(response.previous_answers?.services).forEach(keep);

  return rows;
};

/**
 * The lists the dropdowns offer, out of a response that carries each at the
 * top level.
 *
 * There is no profession list among them: that field falls back to the form's
 * own suggestions and goes up as text. The allergen list is served, and falls
 * back to a list of this form's own where it is not — see
 * `PRECONSULTATION_FALLBACK_OPTIONS`, which says what that costs.
 *
 * The Goal field is fed by the practice's own services now rather than by a
 * catalog of goals: what a client is signed up against is a service the
 * practitioner offers, and it carries a `public_id` where a catalog row
 * carries a number. `goals` is still read behind it for a backend that has not
 * made that move — the field is drawn the same either way, and only the id
 * that goes back up differs. It is the one field with no list of this app's
 * own under it, and `goalRows` says why.
 */
const toCatalogs = (
  response: PreconsultationFormResponse
): PreconsultationCatalogs => {
  const fallback = PRECONSULTATION_FALLBACK_OPTIONS;
  const goals = goalRows(response);

  return {
    goals: catalogOf(goals.length > 0 ? goals : response.goals, []),
    lifeStyles: catalogOf(response.life_styles, fallback.lifeStyles),
    // The backend's name for the list is `dietary_patterns`; the form asks
    // about a food preference. The same list either way.
    foodPreferences: catalogOf(response.dietary_patterns, fallback.foodPreferences),
    cuisines: catalogOf(response.cuisines, fallback.cuisines),
    foodAllergies: catalogOf(response.food_allergies, fallback.foodAllergies),
    medicalConditions: catalogOf(
      response.medical_conditions,
      fallback.medicalConditions
    ),
    // Never served, so always the form's own list, and always as text.
    professions: catalogOf(null, fallback.professions),
  };
};

/**
 * Which sections this practitioner asks, out of `field_config`.
 *
 * Anything missing is read as asked. A backend that has not grown this yet
 * sends no config at all, and the form drew every section before it existed —
 * so that is what an absent flag has to mean. Only an explicit `false` takes a
 * section off.
 */
const asked = (value: boolean | null | undefined): boolean => value !== false;

const toFields = (
  config: PreconsultationFieldConfigResponse | null | undefined
): PreconsultationFields => {
  const shown = config ?? {};

  return {
    location: asked(shown.show_location),
    foodPreference: asked(shown.show_food_preference),
    favoriteDishes: asked(shown.show_favorite_dishes),
    foodsToAvoid: asked(shown.show_foods_to_avoid),
    profession: asked(shown.show_profession),
    dailyRoutine: asked(shown.show_daily_routine),
    lifestyleHabits: asked(shown.show_lifestyle_habits),
    medicalConditions: asked(shown.show_medical_conditions),
    medications: asked(shown.show_medications),
  };
};

/**
 * What the workspace already holds about this person, as answers the form can
 * open on.
 *
 * Only the fields that are certainly about them. Nothing is invented and
 * nothing half-known is filled in: an empty box is a question, and a box filled
 * with a guess is a wrong answer somebody has to notice before they can correct
 * it.
 */
/**
 * `1998-03-26`, as the `26/03/1998` the date field shows.
 *
 * Empty for anything the wire sends that is not a plain ISO day — a null, a
 * timestamp, a format this app has never seen. An empty box is a question the
 * client can answer; a half-read date is one they have to notice is wrong.
 */
const toFormDate = (iso: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

/** A named row's name, trimmed, and "" for a row that came without one. */
const nameOf = (row: CatalogRow | null | undefined): string =>
  (row?.name ?? "").trim();

/** Every named row of a list, skipping any that came back without a name. */
const namesOf = (
  rows: (CatalogRow | PractitionerServiceRow)[] | null | undefined
): string[] =>
  (rows ?? []).map((row) => (row?.name ?? "").trim()).filter(Boolean);

/** A free list with the blanks dropped — the wire sends `[""]` often enough. */
const listOf = (values: string[] | null | undefined): string[] =>
  (values ?? []).map((value) => (value ?? "").trim()).filter(Boolean);

/** A figure as the string a box holds, and "" for anything that is not one. */
const figureOf = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const text = `${value}`.trim();
  return text && Number.isFinite(Number(text)) ? text : "";
};

/** `09:00:00` as the `09:00` the time field holds. "" for anything else. */
const clockOf = (value: string | null | undefined): string => {
  const held = (value ?? "").trim();
  return /^\d{2}:\d{2}/.test(held) ? held.slice(0, 5) : "";
};

/**
 * The two blocks share every field about the person themselves, under the same
 * names — `existing_details` is what the workspace holds, `previous_answers` is
 * what they last said — so both are read by this.
 *
 * Only what is actually there is taken. An empty string is a question nobody
 * answered rather than an answer of nothing, and writing one over what the
 * record knows would blank a box that was rightly filled.
 */
type PersonResponse = PreconsultationKnownResponse &
  PreconsultationPreviousAnswersResponse;

const toPerson = (held: PersonResponse): Partial<PreconsultationAnswers> => {
  const answers: Partial<PreconsultationAnswers> = {};

  if (held.first_name?.trim()) answers.firstName = held.first_name.trim();
  if (held.last_name?.trim()) answers.lastName = held.last_name.trim();
  if (held.date_of_birth?.trim()) {
    const born = toFormDate(held.date_of_birth);
    if (born) answers.dob = born;
  }
  if (held.age != null && `${held.age}`.trim()) {
    answers.age = `${held.age}`.trim();
  }
  if (held.gender?.trim()) {
    // Sentence case, because that is how the two buttons on the form are
    // labelled and how a selected one is recognised. The backend spells it
    // FEMALE.
    const gender = held.gender.trim().toLowerCase();
    answers.gender = gender.charAt(0).toUpperCase() + gender.slice(1);
  }
  if (held.email?.trim()) answers.email = held.email.trim();
  if (held.phone_number?.trim()) {
    // The ten digits the field holds, whatever shape the number is stored in:
    // +91 78693 69854 and 7869369854 are the same number, and only one of them
    // fits the box.
    answers.phone = held.phone_number.replace(/\D/g, "").slice(-10);
  }

  // Where they live, all four of it. A city used to be left alone because one
  // seeded without a state is a city the box beside it cannot account for —
  // the list it offers is narrowed by the state above. Both arrive together
  // now, so the pair is opened on as a pair.
  if (held.full_address?.trim()) answers.address = held.full_address.trim();
  if (held.state?.trim()) answers.state = held.state.trim();
  if (held.city?.trim()) answers.locationCity = held.city.trim();
  if (held.postal_code?.trim()) answers.pincode = held.postal_code.trim();

  if (held.profession?.trim()) answers.profession = held.profession.trim();
  if (nameOf(held.dietary_pattern)) {
    answers.foodPreference = nameOf(held.dietary_pattern);
  }
  if (nameOf(held.life_style)) answers.lifestyle = nameOf(held.life_style);

  // Every one of them. An allergy left out of a list the client is shown back
  // is one they will assume their dietitian already has.
  const allergies = namesOf(held.food_allergies);
  if (allergies.length > 0) answers.foodAllergies = allergies;

  // What they were signed up against: the practice's own services now, and a
  // catalog of goals on a backend that has not made that move. All of them —
  // the field takes several, so a record holding three opens on three rather
  // than on the first with the other two silently dropped, which would read as
  // the client having changed their mind about two of their goals.
  const goals = namesOf(held.services ?? held.goals);
  if (goals.length > 0) answers.goals = goals;

  return answers;
};

const toKnown = (
  response: PreconsultationKnownResponse | null | undefined
): Partial<PreconsultationAnswers> => toPerson(response ?? {});

/**
 * What this person last sent, back in the boxes they typed it into.
 *
 * Only ever present on a form the practitioner returned — see
 * `PreconsultationPreviousAnswersResponse`. It is read on top of what the
 * workspace holds rather than instead of it: these are the client's own words
 * and the more recent of the two, and a form sent back over three wrong
 * answers has to open on the thirty-seven right ones.
 *
 * Units are the one place this is careful. A height is stored in centimetres
 * and a weight in whichever unit it was given, so a figure whose unit this
 * form has no box for is left out rather than shown against the wrong label:
 * `168` under `cm` where the record meant inches is not a cosmetic slip but a
 * different person, and everything a plan is calculated from is downstream of
 * it.
 */
const toPrevious = (
  response: PreconsultationPreviousAnswersResponse | null | undefined
): Partial<PreconsultationAnswers> => {
  const held = response ?? {};
  const answers: Partial<PreconsultationAnswers> = toPerson(held);

  const height = figureOf(held.height_value);
  const heightUnit = (held.height_unit ?? "").trim().toUpperCase();
  // Centimetres or nothing: the submit sends `CM` whichever box was typed in,
  // so that is the only unit a stored height can honestly be read under.
  if (height && (!heightUnit || heightUnit === "CM")) {
    answers.height = height;
    answers.heightUnit = "cm";
  }

  const weight = figureOf(held.weight_value);
  const weightUnit = (held.weight_unit ?? "").trim().toUpperCase();
  if (weight && (weightUnit === "KG" || weightUnit === "LB")) {
    answers.weight = weight;
    answers.weightUnit = weightUnit === "LB" ? "lb" : "kg";
  }

  const target = figureOf(held.target_weight_value);
  const targetUnit = (held.target_weight_unit ?? "").trim().toUpperCase();
  if (target && (targetUnit === "KG" || targetUnit === "LB")) {
    answers.targetWeight = target;
    answers.targetWeightUnit = targetUnit === "LB" ? "lb" : "kg";
  }

  const cuisines = namesOf(held.favorite_cuisines);
  if (cuisines.length > 0) answers.cuisines = cuisines;

  const loved = listOf(held.favorite_dishes);
  if (loved.length > 0) answers.dishesLoved = loved;
  const avoided = listOf(held.foods_to_avoid);
  if (avoided.length > 0) answers.dishesAvoided = avoided;

  if (clockOf(held.working_hours_start)) {
    answers.workFrom = clockOf(held.working_hours_start);
  }
  if (clockOf(held.working_hours_end)) {
    answers.workTo = clockOf(held.working_hours_end);
  }
  // The flag travels whichever way it was answered: `false` is "there are
  // fixed hours" and is as much an answer as `true`. Only a `null` — nobody
  // reached the question — is left to the form's own default.
  if (held.has_no_fixed_working_hours != null) {
    answers.workAnyHours = !!held.has_no_fixed_working_hours;
  }
  if (clockOf(held.sleep_start)) answers.sleepFrom = clockOf(held.sleep_start);
  if (clockOf(held.sleep_end)) answers.sleepTo = clockOf(held.sleep_end);
  if (held.has_no_fixed_sleep_schedule != null) {
    answers.sleepAnyHours = !!held.has_no_fixed_sleep_schedule;
  }

  // The three habit constants are this app's own, so what was stored is what
  // the option lists already hold — nothing to translate, only to trim.
  if (held.travel_frequency?.trim()) answers.travel = held.travel_frequency.trim();
  if (held.alcohol_frequency?.trim()) {
    answers.alcohol = held.alcohol_frequency.trim();
  }
  if (held.smoking_frequency?.trim()) {
    answers.smoking = held.smoking_frequency.trim();
  }

  const conditions = namesOf(held.medical_conditions);
  if (conditions.length > 0) answers.conditions = conditions;
  // `null` is the question nobody was put, and it is not a No — the same
  // distinction `takes_medications` keeps going the other way.
  if (held.takes_medications != null) {
    answers.onMedication = held.takes_medications ? "Yes" : "No";
  }
  const medications = listOf(held.medications);
  if (medications.length > 0) answers.medications = medications;

  return answers;
};

const toForm = (
  response: PreconsultationFormResponse
): PreconsultationForm => ({
  brand: {
    // Their brand name, then their own, then nothing — which the shell reads
    // as "draw no lockup". Never a fallback to Naurist's mark: the person
    // reading this is answering their dietitian.
    name:
      response.workspace?.brand_name?.trim() ||
      response.workspace?.contact_name?.trim() ||
      "",
    // Stored paths are rooted at the API's origin, not at this page's — the
    // form is served from the practitioner's own subdomain and the media from
    // the backend. See `mediaUrl`.
    logoUrl: response.workspace?.logo?.url
      ? mediaUrl(response.workspace.logo.url)
      : "",
  },
  // Always. The wire carries no state any more: a link that is spent, expired
  // or never existed is a 404, which the hook above reads as missing. Anything
  // that answered at all is a form to fill in.
  state: "OPEN",
  catalogs: toCatalogs(response),
  fields: toFields(response.field_config),
  // What the workspace holds, and over it whatever they last sent on a form
  // that was returned to them. Their own answers are the more recent of the
  // two and the ones they will recognise; an empty field in that block is not
  // an answer, so it never blanks what the record already knew.
  known: {
    ...toKnown(response.existing_details),
    ...toPrevious(response.previous_answers),
  },
  recipientName: (response.recipient_name ?? "").trim(),
});

/**
 * The form behind one link.
 *
 * The practitioner's slug as well as the token: the link is one address, and
 * this is the read behind it — `ayushisahuu.naurist.com/i/<token>` is served
 * by `/public/pre-consultation/ayushisahuu/<token>/`. The slug comes from the
 * address bar rather than from anything stored; see `formHandleFromHost`.
 *
 * A token that was never valid, one already filled in, and one that has run
 * out all come back 404, and the screen draws the same notice for each.
 * Telling them apart is not this app's to do and would not be worth doing: it
 * is the one difference a stranger could learn something from.
 */
export async function fetchPreconsultationForm(
  handle: string,
  token: string,
  signal?: AbortSignal
): Promise<PreconsultationForm> {
  const response = await api.get<PreconsultationFormResponse>(
    API_ENDPOINTS.preconsultation.form(handle, token),
    { signal }
  );

  return toForm(response);
}

/** A number out of a box, or `null` for one left empty or half-typed. */
const numberOf = (value: string): number | null => {
  const text = value.trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

/** The row a displayed name came from, or nothing. */
const rowOf = (
  catalog: PreconsultationCatalog,
  name: string
): CatalogRow | undefined => {
  const held = name.trim();
  if (!held) return undefined;

  return catalog.rows.find(
    (option) => option.name.trim().toLowerCase() === held.toLowerCase()
  );
};

/**
 * The catalog id behind a name the reader picked, or `null` where there is
 * none.
 *
 * `null` covers three cases and they are the same on the wire: a question left
 * unanswered, an answer that is not in the catalog, and a row identified by
 * something that is not a catalog id. The middle one cannot happen through the
 * form — the id-backed fields are closed lists for exactly this reason — but a
 * catalog edited on the backend while a form sat open would reach here, and
 * sending nothing is the only honest thing left to do with it.
 *
 * The last is the strictness the practitioner's side keeps too: these four
 * fields name rows of the backend's own catalogs, and a `public_id` arriving
 * on one of them would name a different record entirely.
 */
const idOf = (catalog: PreconsultationCatalog, name: string): number | null => {
  const id = rowOf(catalog, name)?.id;
  return typeof id === "number" ? id : null;
};

/**
 * Every name a multi-answer field holds, as the catalog ids behind them. One
 * that matches no row is dropped rather than sent as a name the endpoint
 * cannot resolve — see `idOf`.
 */
const idsOfAll = (
  catalog: PreconsultationCatalog,
  names: string[]
): number[] =>
  names
    .map((name) => idOf(catalog, name))
    .filter((id): id is number => id !== null);

/**
 * Every name a multi-answer field holds, as whatever identifies the rows
 * behind them — which for the Goal field is a service's `public_id` string.
 *
 * One that matches no row is dropped rather than sent as anything, on the same
 * reading as `idsOfAll`: what goes up has to name a record, so a name the backend
 * has never heard of has nothing to be sent as. What survives is still the
 * answer, minus a goal the list was reloaded out from under.
 */
const referenceIdsOf = (
  catalog: PreconsultationCatalog,
  names: string[]
): (number | string)[] =>
  names
    .map((name) => rowOf(catalog, name)?.id)
    .filter((id): id is number | string => id !== undefined && id !== "");

/** `26/03/1998` as the `1998-03-26` the endpoint takes, and `null` for a box
 *  left empty or half-typed — the mirror of `toFormDate`. */
const isoDateOf = (typed: string): string | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(typed.trim());
  return match ? `${match[3]}-${match[2]}-${match[1]}` : null;
};

/** `09:00` as the `09:00:00` the endpoint takes. Empty stays `null`. */
const timeOf = (value: string): string | null =>
  /^\d{2}:\d{2}$/.test(value.trim()) ? `${value.trim()}:00` : null;

/**
 * The answers, as the endpoint takes them.
 *
 * The catalogs come in beside the answers because most of the translation
 * needs them: the dropdowns hold the names they displayed, and five of the
 * fields go up as the ids behind those names. See `PreconsultationPayload`,
 * which is where the shape of all this is spelled out.
 *
 * Times go up as `null` where "No fixed hours" was ticked, with the flag
 * beside them set — somebody who keeps no fixed hours has given an answer, and
 * a blank pair without the flag would read as a question nobody reached. The
 * two are different things and a practitioner acts on them differently.
 *
 * Two keys can be missing from what this returns rather than empty:
 * `favorite_cuisine_ids` and `goal_ids`. The endpoint replaces those lists with
 * whatever it is handed, so `[]` is not a quiet answer but an instruction to
 * clear them — see the payload type, where that is argued out. Nothing else
 * here is conditional: every other question this form asks goes up answered or
 * blank, and blank is a fact about the form rather than about the record.
 */
export const toPreconsultationPayload = (
  answers: PreconsultationAnswers,
  catalogs: PreconsultationCatalogs
): PreconsultationPayload => {
  const cuisines = idsOfAll(catalogs.cuisines, answers.cuisines);
  // Service `public_id`s, under the key that named goals before a goal became
  // one of the practice's services — see `goal_ids` on the payload type.
  const goals = referenceIdsOf(catalogs.goals, answers.goals);

  return {
    first_name: answers.firstName.trim(),
    last_name: answers.lastName.trim(),
    date_of_birth: isoDateOf(answers.dob),
    age: numberOf(answers.age),
    // `Female` on the two buttons, `FEMALE` on the wire.
    gender: answers.gender.trim().toUpperCase(),
    email: answers.email.trim(),
    phone_number: answers.phone,

    full_address: answers.address.trim(),
    state: answers.state.trim(),
    // One box now, on the location step. The basics step used to ask for a city
    // too, as the fallback for somebody never shown the location step; with that
    // question gone, a practitioner who turns off `show_location` collects no
    // city at all — which is what turning off the location questions means.
    city: answers.locationCity.trim(),
    postal_code: answers.pincode.trim(),

    // Centimetres whichever box it was typed in — the endpoint takes `CM`, and
    // a record where every height is in one unit is one where two of them can
    // be compared. The feet the box offers stop at `heightInCm`.
    height_value: numberOf(heightInCm(answers.height, answers.heightUnit)),
    height_unit: "CM",
    // A weight keeps the unit it was given: both are taken, and what the client
    // said is what the practitioner should read back.
    weight_value: numberOf(answers.weight),
    weight_unit: answers.weightUnit.toUpperCase(),
    target_weight_value: numberOf(answers.targetWeight),
    target_weight_unit: answers.targetWeightUnit.toUpperCase(),

    dietary_pattern: idOf(catalogs.foodPreferences, answers.foodPreference),
    life_style: idOf(catalogs.lifeStyles, answers.lifestyle),
    medical_condition_ids: idsOfAll(
      catalogs.medicalConditions,
      answers.conditions
    ),

    favorite_dishes: answers.dishesLoved,
    foods_to_avoid: answers.dishesAvoided,
    food_allergy_ids: idsOfAll(catalogs.foodAllergies, answers.foodAllergies),

    profession: answers.profession.trim(),

    working_hours_start: answers.workAnyHours ? null : timeOf(answers.workFrom),
    working_hours_end: answers.workAnyHours ? null : timeOf(answers.workTo),
    has_no_fixed_working_hours: answers.workAnyHours,
    sleep_start: answers.sleepAnyHours ? null : timeOf(answers.sleepFrom),
    sleep_end: answers.sleepAnyHours ? null : timeOf(answers.sleepTo),
    has_no_fixed_sleep_schedule: answers.sleepAnyHours,

    // Picked from the backend's own choices, so they go up exactly as they were
    // picked — there is no wording of this app's left to translate.
    travel_frequency: answers.travel.trim(),
    alcohol_frequency: answers.alcohol.trim(),
    smoking_frequency: answers.smoking.trim(),

    // `null` rather than `false` where the question was skipped: "not on any
    // medication" and "did not say" are different things to read off a health
    // record.
    takes_medications: answers.onMedication
      ? answers.onMedication === "Yes"
      : null,
    medications: answers.medications,

    // Last, and only where there is something to say. See the note above.
    ...(cuisines.length > 0 ? { favorite_cuisine_ids: cuisines } : {}),
    ...(goals.length > 0 ? { goal_ids: goals } : {}),
  };
};

/**
 * Sends them, and spends the token.
 *
 * A 400 arrives carrying whatever the backend objected to. A 404 or a 409
 * means the link stopped working between the form being opened and being sent
 * — a second tab, a form finished twice — and the page says so rather than
 * offering a retry that cannot succeed.
 *
 * What comes back is the form as it now stands, `SUBMITTED`, with every answer
 * on it as the backend stored them. The page that calls this does not read it
 * — the person who filled the form gets a thank-you and nothing to return to —
 * but it is mapped and handed back rather than thrown away, because it is the
 * only moment this app is told what was actually recorded. `SUBMITTED` is the
 * fallback for a state this app has not heard of, that being the one thing a
 * form this call just answered can be.
 */
export async function submitPreconsultation(
  handle: string,
  token: string,
  payload: PreconsultationPayload,
  signal?: AbortSignal
): Promise<PreconsultationFormSummary> {
  try {
    return toPreconsultationFormSummary(
      await api.post<PreconsultationFormRecord>(
        API_ENDPOINTS.preconsultation.submit(handle, token),
        payload,
        { signal }
      ),
      "SUBMITTED"
    );
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/** Whether a failure means the link is finished rather than the network is. */
export const isSpentLink = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 404 || error.status === 409);
